import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { getOpenItems, createOpenItem, updateOpenItem, deleteOpenItem } from "@/services/ccApi";

const STATUSES = ["Investigating", "Pending", "Blocked", "Built", "Done"];
const PRIORITIES = ["High", "Medium", "Low"];
const statusCls = {
  Investigating: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Blocked: "bg-red-50 text-red-700 border-red-200",
  Built: "bg-cyan-50 text-cyan-700 border-cyan-200",
  Done: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const prioCls = { High: "text-red-600", Medium: "text-amber-600", Low: "text-slate-500" };
const empty = { title: "", system: "ops", description: "", status: "Pending", priority: "Medium", blocked_by: "", owner: "" };
const errMsg = (e) => e?.response?.data?.detail || e?.message || "Failed";

export default function OpenItems() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  const reload = () => getOpenItems().then(setItems).catch(() => {});
  useEffect(() => { reload(); }, []);

  const sorted = useMemo(() => {
    const pr = { High: 0, Medium: 1, Low: 2 };
    return [...items].sort((a, b) => (pr[a.priority] ?? 3) - (pr[b.priority] ?? 3));
  }, [items]);
  const rows = sorted.filter((i) => filter === "all" || i.status === filter);

  const save = async () => {
    if (!form.title) return toast.error("Item title required");
    try { await createOpenItem(form); toast.success("Open item created"); setForm(empty); setOpen(false); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (item, status) => { try { await updateOpenItem(item.id, { status }); setItems((xs) => xs.map((x) => (x.id === item.id ? { ...x, status } : x))); toast.success(`Marked ${status}`); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (id) => { try { await deleteOpenItem(id); setItems((xs) => xs.filter((x) => x.id !== id)); toast.success("Removed"); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <PageHeader title="Open Items Tracker" subtitle="A persistent cross-system operational to-do list so unresolved work (bugs, gaps, follow-ups across the Customer app, Pro app, Cloud Functions and Ops) never lives only in chat. Stored in the Command Center."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" className="gap-1.5" data-testid="add-item-btn"><Icons.Plus className="h-4 w-4" /> New item</Button></DialogTrigger>
            <DialogContent data-testid="add-item-dialog">
              <DialogHeader><DialogTitle className="font-heading">New open item</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5"><Label>Item</Label><Input data-testid="item-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>System</Label><Input data-testid="item-system" value={form.system} onChange={(e) => setForm({ ...form, system: e.target.value })} placeholder="web / home / pro / functions / ops" /></div>
                  <div className="space-y-1.5"><Label>Owner</Label><Input data-testid="item-owner" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></div>
                </div>
                <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="item-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="item-status"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div className="space-y-1.5"><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger data-testid="item-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Blocked by</Label><Input data-testid="item-blocked" value={form.blocked_by} onChange={(e) => setForm({ ...form, blocked_by: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="item-submit">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex flex-wrap gap-2 mb-4">
        <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")} data-testid="item-filter-all">All ({items.length})</Button>
        {STATUSES.map((s) => <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} data-testid={`item-filter-${s}`}>{s} ({items.filter((i) => i.status === s).length})</Button>)}
      </div>

      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>System</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead>Blocked by</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((it) => (
              <TableRow key={it.id} data-testid={`item-row-${it.id}`}>
                <TableCell>
                  <p className="font-medium text-foreground">{it.title}</p>
                  {it.description && <p className="text-xs text-muted-foreground max-w-md">{it.description}</p>}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{it.system}</TableCell>
                <TableCell className={`text-sm font-medium ${prioCls[it.priority] || ""}`}>{it.priority}</TableCell>
                <TableCell>
                  <Select value={it.status} onValueChange={(v) => setStatus(it, v)}>
                    <SelectTrigger className={`h-7 w-[140px] text-xs border ${statusCls[it.status] || ""}`} data-testid={`item-setstatus-${it.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{it.blocked_by || "—"}</TableCell>
                <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(it.id)} data-testid={`item-remove-${it.id}`}><Icons.Trash2 className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No open items in this view. Add one to start tracking.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
